import { Component, ComponentChild } from 'preact'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import Panel from '../../control-components/panel'
import { fetchPhrase } from '../../libs/translationsUtils/phrase'
import Category from './Category'
import CategoryDefaultSetting from './CategoryDefaultSetting'

const SortableList = SortableContainer(({ children }: { children: ComponentChild }) => (
  <ul class='sortable-curated-topics-list'>{children}</ul>
))
const SortableItem = SortableElement(({ children }: { children: ComponentChild }) => (
  <li class='sortable-curated-topic'>{children}</li>
))

interface FallbackPhrases {
  categories?: string
  recent_active?: string
  unanswered?: string
}

interface ReorderedTabsResult {
  oldIndex: number
  newIndex: number
}

const fetchPhraseByTab = async (tab: Tab): Promise<string | undefined> => {
  return await new Promise((resolve) => {
    fetchPhrase('Forum', `tab.navigation.${tab.phrase_suffix}`, (phrase) => {
      resolve(phrase)
    })
  })
}

const getName = (phraseSuffix: string) => {
  switch (phraseSuffix) {
    case 'categories':
      return 'Category'
    case 'recent_active':
      return 'Recently active'
    case 'unanswered':
      return 'Help others'
  }
  return ''
}

export interface Tab {
  class: string
  collapsed: boolean
  enabled: boolean
  phrase: string
  phrase_suffix: 'categories' | 'recent_active' | 'unanswered'
}

interface Props {
  tabs: Tab[]
  class?: string
  onChange: (state: { tabs: Tab[] }) => void
}

interface State {
  tabs: Tab[]
  fallbackPhrases: FallbackPhrases
}

export class TabsConfigurator extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      tabs: props.tabs,
      fallbackPhrases: {}
    }
    this.reorderTabs = this.reorderTabs.bind(this)
  }

  updateTab(idx: number, field: keyof Tab, value: Tab[keyof Tab]) {
    const data = this.state.tabs.map((item, index) => {
      if (idx !== index) return item
      return {
        ...item,
        [field]: value
      }
    })
    this.setState({ tabs: data }, () => {
      this.props.onChange({ tabs: this.state.tabs })
    })
  }

  reorderTabs(result: ReorderedTabsResult) {
    const newOrder = arrayMove(this.state.tabs, result.oldIndex, result.newIndex)
    this.setState({ tabs: newOrder })
    this.props.onChange({ tabs: newOrder })
  }

  async fetchPhrases() {
    const phrases = await Promise.all(
      this.props.tabs.map(async (tab) => await fetchPhraseByTab(tab))
    )
    const fallbackPhrases: FallbackPhrases = {}
    this.props.tabs.forEach((tab, i) => {
      fallbackPhrases[tab.phrase_suffix] = phrases[i]
    })
    this.setState({
      fallbackPhrases: fallbackPhrases
    })
  }

  componentDidMount() {
    this.fetchPhrases()
  }

  render(props: Props, state: State) {
    const categoryTabIdx = state.tabs.findIndex((tab) => tab.phrase_suffix === 'categories')
    const categoryTab = state.tabs[categoryTabIdx]

    return (
      <div class={`tabs-configurator ${props.class}`}>
        <div>
          <label>Tabs settings</label>
          <p>
            You can customise tabs by reordering, renaming, and setting visibility. <br />
            <strong>Tip:</strong> If you use quick links for your categories, it is recommended to
            not use the category tab as the first tab
          </p>
        </div>
        <div class='table'>
          <div class='tab head'>
            <label>Tab</label>
            <label>Tab name</label>
            <label>Visibility</label>
          </div>
          <SortableList useDragHandle onSortEnd={this.reorderTabs}>
            {state.tabs.map((tab, idx) => (
              <SortableItem index={idx} key={idx}>
                <Category
                  title={getName(tab.phrase_suffix)}
                  phrase={tab.phrase}
                  placeholder={state.fallbackPhrases[tab.phrase_suffix] || ''}
                  enabled={tab.enabled}
                  onChange={(value: Tab[keyof Tab], field: keyof Tab) => {
                    return this.updateTab(idx, field, value)
                  }}
                />
              </SortableItem>
            ))}
          </SortableList>
        </div>
        {Boolean(categoryTab.enabled) && (
          <CategoryDefaultSetting
            collapsed={categoryTab.collapsed}
            onChange={(value: boolean) => this.updateTab(categoryTabIdx, 'collapsed', value)}
          />
        )}
      </div>
    )
  }
}

interface PanelProps extends Props {
  dragHandle: JSX.Element | null
  switcher: JSX.Element
}

const TabsConfiguratorPanel = (props: PanelProps) => {
  return (
    <Panel
      collapsible
      customClass='homepage-widget'
      title={
        <div className='panel-title-with-switch'>
          <div className='homepage-reorderable-panel-title'>
            {props.dragHandle}
            <span>Community category & discussions</span>
          </div>
          {props.switcher}
        </div>
      }
    >
      <TabsConfigurator {...props} />
    </Panel>
  )
}

export default TabsConfiguratorPanel
